import React, { useCallback, useContext, useMemo, useState } from "react";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { DirectorySort } from "@common/constants/counterparty";
import { RegisteredCounterpartyIncludeScope } from "@common/helpers/counterparties";
import { useWorkspaceContext } from "../../../state/workspaceContext";
import { ButtonWithLoader } from "../../general/ButtonWithLoader/ButtonWithLoader";
import { useCategoryLabelGetter } from "../../../hooks/useCategoryLabelGetter";
import { TransactionRuleCreationContext } from "../TransactionRuleCreation/TransactionRuleCreation.context";
import { pluralize } from "../../../common/helpers/string";
import {
    RuleActionType,
    RuleConditionType,
    RuleSource,
    TransactionRuleActionDefinition,
    TransactionRuleConditionDefinition,
} from "../../../common/types/transactionRule";
import { Button } from "../../general/Button/Button";
import { useCreateRule } from "../../../api/rules.api";
import { useCounterpartiesQuery } from "../../../api/counterparty.api";
import styles from "./RuleCreationToast.module.scss";

interface RuleCreationToastData {
    conditions: TransactionRuleConditionDefinition[];
    actions: TransactionRuleActionDefinition[];
    transactionsNumber: number;
    canCreateWithtoutReview: boolean;
    applyToExistingTransactions?: boolean;
}
export const RuleCreationToast: React.FC<RuleCreationToastData> = ({
    conditions,
    transactionsNumber,
    actions,
    canCreateWithtoutReview,
    applyToExistingTransactions,
}) => {
    const { createRule } = useContext(TransactionRuleCreationContext);
    const labelGetter = useCategoryLabelGetter();

    const { data: counterparties } = useCounterpartiesQuery({
        sort: DirectorySort.NAME_ASC,
        includeRegistered: RegisteredCounterpartyIncludeScope.USED,
        includeUsageStats: false,
    });
    const accounts = useFinancialAccounts();
    const { activeWorkspace } = useWorkspaceContext();
    const [ruleAdded, setRuleAdded] = useState(false);
    const ruleCreation = useCreateRule(() => setRuleAdded(true));

    const conditionLabel = useMemo(() => {
        if (!conditions) {
            return;
        }

        return conditions.map((condition) => {
            let label = "";
            if (condition.conditionType === RuleConditionType.COUNTERPARTY) {
                label += counterparties?.data
                    ?.filter((cp) =>
                        (condition.value as string[]).includes(
                            cp.counterparty.id,
                        ),
                    )
                    ?.map((cp) => cp.counterparty.name)
                    .join(", ");
            }

            if (
                condition.conditionType === RuleConditionType.FINANCIAL_ACCOUNTS
            ) {
                label += accounts
                    ?.filter((account) =>
                        (condition.value as number[]).includes(account.id),
                    )
                    ?.map((account) => account.name)
                    .join(", ");
            }

            return label;
        });
    }, [conditions, counterparties, accounts]);

    const actionLabel = useMemo(() => {
        if (!actions) {
            return;
        }

        return actions.map((action) => {
            if (action.actionType === RuleActionType.CATEGORY) {
                return labelGetter(action.value as string);
            }
        });
    }, [actions, labelGetter]);

    const newRuleDefinition = useMemo(
        () => ({
            conditions,
            actions,
            applyToExistingTransactions,
        }),
        [conditions, actions, applyToExistingTransactions],
    );
    const ruleCreationPayload = useMemo(() => {
        if (!activeWorkspace || !newRuleDefinition) {
            return;
        }

        return {
            workspaceId: activeWorkspace.id,
            applyToExistingTransactions: true,
            rule: newRuleDefinition,
            source: RuleSource.TRANSACTIONS_FEED,
        };
    }, [activeWorkspace, newRuleDefinition]);

    const reviewRule = useCallback(() => {
        if (!newRuleDefinition) {
            return;
        }

        createRule({
            definition: {
                conditions: newRuleDefinition.conditions,
                actions: newRuleDefinition.actions,
                applyToExistingTransactions:
                    newRuleDefinition.applyToExistingTransactions,
            },
            source: RuleSource.TRANSACTIONS_FEED,
            isAccounting: false,
        });
    }, [newRuleDefinition, createRule]);

    return (
        <div className={styles.message}>
            <p>
                Categorize{" "}
                <span className={styles.conditionLabel}>{conditionLabel}</span>{" "}
                as <span className={styles.actionLabel}>{actionLabel}</span>{" "}
                going forward
                {transactionsNumber > 0 ? (
                    <>
                        {" "}
                        and apply to{" "}
                        <strong>
                            {transactionsNumber} similar{" "}
                            {pluralize("transaction", transactionsNumber)}
                        </strong>
                    </>
                ) : null}
                ?
            </p>

            <footer className={styles.footer}>
                {ruleAdded ? (
                    "Rule created"
                ) : (
                    <div className="d-flex gap-buttons">
                        <Button
                            variant="tertiary"
                            onClick={reviewRule}
                            className={styles.review}
                        >
                            Review
                        </Button>
                        {canCreateWithtoutReview ? (
                            <ButtonWithLoader
                                variant="secondary"
                                onClick={() => {
                                    if (!ruleCreationPayload) {
                                        return;
                                    }

                                    ruleCreation.mutate(ruleCreationPayload);
                                }}
                                loading={ruleCreation.isPending}
                            >
                                Create rule
                            </ButtonWithLoader>
                        ) : null}
                    </div>
                )}
            </footer>
        </div>
    );
};
